import {
    differenceInDays,
    format,
    isToday,
    isYesterday,
    parseISO,
} from 'date-fns';
import { formatCompactDuration, formatEventTime, padZero } from './time';
import { SourceType } from '@/types/source';
import i18n from '@/lib/i18n';
import { I18nNamespace } from '@/lib/i18n-namespaces';

const isVideoTimeRange = (sourceType?: SourceType): boolean => {
    return sourceType === SourceType.FILE;
};

const isVideoRangeEvent = (
    eventStartTime: number | undefined,
    eventEndTime: number | undefined,
    sourceType?: SourceType,
): boolean => {
    return (
        eventStartTime !== undefined &&
        eventEndTime !== undefined &&
        isVideoTimeRange(sourceType)
    );
};

const DAY_IN_SECONDS = 24 * 60 * 60;

const secondsToMilliseconds = (seconds: number): number => seconds * 1000;

const millisecondsToSeconds = (milliseconds: number): number =>
    Math.floor(milliseconds / 1000);

type DateInput = number | Date;

interface FormatRelativeTimeOptions {
    sourceType?: SourceType;
    maxRelativeDays?: number;
    nowMs?: number;
}

const translate = (key: string, options?: Record<string, unknown>): string =>
    i18n.t(key, { ns: I18nNamespace.COMMON, ...options });

const toDate = (value: DateInput): Date =>
    value instanceof Date ? value : new Date(value * 1000);

function formatDate(value: DateInput): string {
    const date = toDate(value);
    return format(date, 'dd/MM/yyyy, HH:mm');
}

function formatDateWithSeconds(value: DateInput): string {
    const date = toDate(value);
    return format(date, 'dd/MM/yyyy, HH:mm:ss');
}

function formatUtcDateTime(value: DateInput): string {
    const date = toDate(value);
    return date.toLocaleString('en-GB', {
        timeZone: 'UTC',
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
    });
}

function formatShortMonthDay(value: DateInput): string {
    const date = toDate(value);
    return format(date, 'MMM d');
}

function getTimestampDaysAgo(days: number) {
    const currentTimestampInSeconds = Math.floor(Date.now() / 1000);
    const daysAgoInSeconds = days * DAY_IN_SECONDS;
    return currentTimestampInSeconds - daysAgoInSeconds;
}

const getLast24HoursTimestamp = (): number => {
    return getTimestampDaysAgo(1);
};

const formatCameraEventTime = (timestamp: number) => {
    const eventDate = new Date(timestamp * 1000);
    return format(eventDate, 'HH:mm');
};

const getDatePrefix = (eventDate: Date): string => {
    if (isToday(eventDate)) return translate('time.today');
    if (isYesterday(eventDate)) return translate('time.yesterday');

    const daysAgo = differenceInDays(new Date(), eventDate);
    return daysAgo < 7 ? format(eventDate, 'EEE') : format(eventDate, 'd MMM');
};

const formatEventTimeRange = (
    startTime: number,
    endTime: number,
    sourceType: SourceType,
) => {
    if (sourceType === SourceType.CAMERA) {
        const eventDate = new Date(startTime * 1000);
        const startTimeFormatted = formatCameraEventTime(startTime);
        const endTimeFormatted = formatCameraEventTime(endTime);
        const datePrefix = getDatePrefix(eventDate);

        return `${datePrefix} ${startTimeFormatted} - ${endTimeFormatted}`;
    } else {
        const eventStartTime = formatEventTime(startTime);
        const eventEndTime = formatEventTime(endTime);
        return `${eventStartTime} - ${eventEndTime}`;
    }
};

const toUtcTimestamp = (date: Date): number =>
    Math.floor(date.getTime() / 1000);

const formatRelativeTime = (
    value: DateInput,
    sourceTypeOrOptions?: SourceType | FormatRelativeTimeOptions,
): string => {
    const options =
        typeof sourceTypeOrOptions === 'object'
            ? sourceTypeOrOptions
            : { sourceType: sourceTypeOrOptions };
    const { sourceType, maxRelativeDays = sourceType ? 0 : 0, nowMs } = options;

    if (!value || (typeof value === 'number' && (isNaN(value) || value <= 0))) {
        return translate('time.invalidDate');
    }

    const eventDate = toDate(value);

    if (isNaN(eventDate.getTime())) {
        return translate('time.invalidDate');
    }

    const now = nowMs !== undefined ? new Date(nowMs) : new Date();
    const diffMs = now.getTime() - eventDate.getTime();
    const diffSeconds = Math.floor(diffMs / 1000);
    const diffMinutes = Math.floor(diffSeconds / 60);
    const diffHours = Math.floor(diffMinutes / 60);
    const diffDays = Math.floor(diffHours / 24);
    const isFutureOrNow = diffMs < 0 || diffMinutes < 1;
    const isRelative =
        isFutureOrNow ||
        isToday(eventDate) ||
        (maxRelativeDays > 0 && diffDays <= maxRelativeDays);
    let formattedTime: string;

    if (isRelative) {
        if (isFutureOrNow) {
            formattedTime = translate('time.justNow');
        } else if (diffMinutes < 60) {
            formattedTime = translate('time.minutesAgo', {
                count: diffMinutes,
            });
        } else if (diffHours < 24) {
            formattedTime = translate('time.hoursAgo', { count: diffHours });
        } else if (isYesterday(eventDate)) {
            formattedTime = translate('time.yesterday');
        } else {
            formattedTime = translate('time.daysAgo', { count: diffDays });
        }
    } else {
        formattedTime = formatDate(eventDate);
    }

    if (sourceType) {
        const prefixKey =
            sourceType === SourceType.CAMERA
                ? isRelative
                    ? 'added'
                    : 'addedOn'
                : isRelative
                  ? 'uploaded'
                  : 'uploadedOn';
        const prefix = i18n.t(prefixKey, { ns: I18nNamespace.SOURCES });
        return `${prefix} ${formattedTime}`;
    }

    return formattedTime;
};

const formatRelativeTimeI18n = (value: DateInput, nowMs?: number): string =>
    formatRelativeTime(value, { maxRelativeDays: 6, nowMs });

const formatChartDateMinutely = (dateString: string): string => {
    try {
        const date = parseISO(dateString);
        return format(date, 'HH:mm');
    } catch {
        return dateString;
    }
};

const formatChartDate3Hourly = (dateString: string): string => {
    try {
        const date = parseISO(dateString);
        return format(date, 'd MMM HH:mm');
    } catch {
        return dateString;
    }
};

const formatChartDateDaily = (dateString: string): string => {
    try {
        const date = parseISO(dateString);
        return format(date, 'd MMM');
    } catch {
        return dateString;
    }
};

const formatChartDateMonthly = (dateString: string): string => {
    try {
        const date = parseISO(dateString);
        return format(date, 'MMM');
    } catch {
        return dateString;
    }
};

const formatEventTimeWithDuration = (
    startTime: number,
    endTime: number,
): string => {
    return translate('time.detectedAt', {
        time: formatEventTime(startTime),
        duration: formatCompactDuration(endTime - startTime),
    });
};

const formatDetectedAtTime = (startTime: number): string =>
    translate('time.detectedAtTime', { time: formatEventTime(startTime) });

const formatEventTimeCompact = (startTime: number, endTime: number): string => {
    return `${formatEventTime(startTime)} (${formatCompactDuration(endTime - startTime)})`;
};

const formatTimeFromDate = (date?: Date, defaultTime = '00:00'): string => {
    if (!date) return defaultTime;
    return `${padZero(date.getHours())}:${padZero(date.getMinutes())}`;
};

const createDateTime = (date: Date, time: string, isEndOfDay = false): Date => {
    const [hours, minutes] = time.split(':').map(Number);
    const dateTime = new Date(date);
    dateTime.setHours(
        hours,
        minutes,
        isEndOfDay ? 59 : 0,
        isEndOfDay ? 999 : 0,
    );
    return dateTime;
};

export {
    secondsToMilliseconds,
    millisecondsToSeconds,
    formatDate,
    formatDateWithSeconds,
    formatUtcDateTime,
    formatShortMonthDay,
    getTimestampDaysAgo,
    getLast24HoursTimestamp,
    formatCameraEventTime,
    formatEventTimeRange,
    toUtcTimestamp,
    formatRelativeTime,
    formatRelativeTimeI18n,
    formatChartDateMinutely,
    formatChartDate3Hourly,
    formatChartDateDaily,
    formatChartDateMonthly,
    formatEventTimeWithDuration,
    formatDetectedAtTime,
    formatEventTimeCompact,
    formatTimeFromDate,
    createDateTime,
    isVideoTimeRange,
    isVideoRangeEvent,
};
